/* eslint-disable no-nested-ternary */
/* eslint-disable import/no-cycle */
import {
  AutoComplete,
  Button,
  Form,
  Radio,
  SearchIcon,
  Typography,
  useSnackbar,
} from "@kitbird/core";
import React, { useEffect, useMemo, useRef } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import Joi from "joi";
import { createPortal } from "react-dom";
import { useQuery } from "react-query";
import CostumerService from "../../../../../assets/images/customer_service.png";
import { ReactComponent as PlusIcon } from "../../../../../assets/icons/plus.svg";

import Avatar from "../../../../../assets/images/avatar.svg";
import Background from "../../../../../assets/images/right-corner-simple-bg.svg";
import {
  Footer,
  RadioGroupWrapper,
  RadioWrapper,
  ReplacementUserSection,
  UserInformation,
  UserInformationText,
  UserPlacement,
} from "./userPosition.styled";
import useFormCreateUser from "../../hooks/useFormCreateUser";
import { type IUser } from "../../../../../services/userAccountService";
import UserPositionMultipleForm from "./UserPositionMultipleForm";
import {
  getUserPosition,
  postUserPositionlist,
  putUserPosition,
} from "../../../../../services/userPositionService/userPositionService";
import { PlacementFields } from "../CreateUserModal";
import { IGetUserPositionData } from "../../../../../services/userPositionService/types";
import ConfirmationModal from "../ConfirmationModal";
import { useUserPosition } from "../../Provider";
import TableCurrentPositions from "../MutationUser/TableCurrentPositions";
import useAuth from "../../../../../components/Authentication/useAuth";

const schemaLabelValue = Joi.object({
  label: Joi.string(),
  value: Joi.string(),
});

const createSchema = (isEditMode: boolean) => Joi.object({
  type_code: Joi.string().required(),
  placement: isEditMode
    ? Joi.optional()
    : Joi.array().items(
      Joi.object({
        area: schemaLabelValue,
        pool: schemaLabelValue,
        department: schemaLabelValue,
        position: schemaLabelValue,
        segment: Joi.when("position", {
          is: Joi.object({
            label: Joi.string().valid("Kabag", "Kapool"),
          }).unknown(),
          then: Joi.optional(),
          otherwise: schemaLabelValue,
        }),
        roles: schemaLabelValue,
      }),
    ),
  replacement_user: isEditMode
    ? Joi.object({
      nip: Joi.string().required().messages({
        "string.empty": "NIP wajib diisi",
        "any.required": "NIP wajib diisi",
      }),
      nama: Joi.string().required().messages({
        "string.empty": "Nama wajib diisi",
        "any.required": "Nama wajib diisi",
      }),
    })
      .required()
      .messages({
        "any.required": "Petugas pengganti wajib diisi",
      })
    : Joi.optional(),
});

const UserPositionForm = ({
  setOpen,
  defaultData,
}: {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  defaultData?: IGetUserPositionData;
}) => {
  const { setConfirmationModal } = useUserPosition();
  const { userAccount } = useAuth();
  const [inputValue, setInputValue] = React.useState("");
  const [valueUser, setValueUser] = React.useState<IUser | undefined>();
  const [isEditMode, setIsEditMode] = React.useState<boolean>(false);
  const refAutoComplete = useRef<HTMLInputElement>(null);
  const snackbar = useSnackbar();

  const labelOptionValue = {
    label: "",
    value: "",
  };

  const schema = useMemo(() => createSchema(isEditMode), [isEditMode]);
  const resolver = useMemo(() => joiResolver(schema), [schema]);

  const {
    handleSubmit,
    control,
    getValues,
    formState: { isValid },
    reset,
    setValue,
  } = useForm<PlacementFields>({
    mode: "onChange",
    resolver,
    defaultValues: {
      type_code: "",
      placement: [
        {
          area: labelOptionValue,
          pool: labelOptionValue,
          department: labelOptionValue,
          position: labelOptionValue,
          segment: labelOptionValue,
          roles: labelOptionValue,
        },
      ],
      replacement_user: {
        nip: "",
        nama: "",
      },
    },
  });

  const {
    listUser,
    listAreas,
    listPool,
    listDepartment,
    listJobTitle,
    listSegments,
    listRoles,
    mutateUser,
    updateUser,
  } = useFormCreateUser(inputValue);

  const listSegment = [
    {
      label: "Taxi Reguler",
      value: "A",
    },
    {
      label: "Taxi Executive",
      value: "B",
    },
    {
      label: "Limousine Kontrak",
      value: "C",
    },
    {
      label: "Limousine Harian",
      value: "D",
    },
    {
      label: "Bus Kontrak",
      value: "E",
    },
    {
      label: "Bus Harian",
      value: "F",
    },
  ];

  const { data: currentPositionsData, isLoading: isLoadingCurrentPositions } = useQuery({
    queryKey: ["current_positions", defaultData?.employee_email],
    queryFn: () => getUserPosition(defaultData?.employee_email || ""),
    enabled: !!defaultData?.employee_email && isEditMode,
  });

  const currentPositions = useMemo(() => {
    if (!currentPositionsData?.lstmstuserpostition || !listPool?.list_pool) {
      return [];
    }
    return currentPositionsData.lstmstuserpostition.map((position) => {
      const pool = listPool.list_pool.find(
        (p) => p.pool_id === position.pool_id,
      );
      return {
        department: position.department,
        pool: pool?.pool_name || position.pool_id,
        status: position.is_active ? "Aktif" : "Tidak Aktif",
        jobTitle: "",
      };
    });
  }, [currentPositionsData, listPool]);

  useEffect(() => {
    if (defaultData) {
      setIsEditMode(true);
      setValueUser({
        employee_name: defaultData.employee_name,
        employee_email: defaultData.employee_email,
        employee_id: defaultData.employee_id,
        id: defaultData.id,
        isActive: defaultData.is_active,
      });
      setValue("type_code", defaultData.code_type);
    } else {
      setIsEditMode(false);
    }
  }, [defaultData, setValue]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "placement",
  });

  const labelValue = useMemo(() => {
    if (listUser?.user && listUser?.user.length > 0) {
      const tempList = listUser.user.map((item) => ({
        label: `${item.employee_id} • ${item.employee_email}`,
        value: `${item.employee_id}`,
      }));
      return tempList;
    }
    return [];
  }, [listUser]);

  const submitUser = () => {
    const tempVal = getValues();
    const tempData = tempVal.placement.map((item) => ({
      employee_name: valueUser?.employee_name as string,
      employee_id: valueUser?.employee_id as string,
      code_type: tempVal.type_code,
      area_id: item.area.value as string,
      pool_id: item.pool.value as string,
      department: item.department.value,
      job_title: item.position.value,
      code_segment: item.segment?.value,
      roles: item.roles?.value as string,
      create_by: userAccount?.employee_name || "ADMIN",
    }));
    const postUser: postUserPositionlist = {
      assignment_users: tempData,
    };
    const putUser: putUserPosition = {
      id: valueUser?.id as number,
      employee_email: valueUser?.employee_email as string,
      employee_id: tempVal.replacement_user?.nip || "",
      employee_name: tempVal.replacement_user?.nama || "",
      update_by: userAccount?.employee_name || "ADMIN",
    };
    if (isEditMode) {
      updateUser.mutate(putUser);
    } else {
      mutateUser.mutate(postUser);
    }
  };

  return (
    <>
      <div
        style={{
          maxHeight: "80vh",
          padding: "1rem 1rem",
          overflow: "auto",
        }}
      >
        {!valueUser || !isEditMode ? (
          <AutoComplete
            dataTestId="nip-input"
            ref={refAutoComplete}
            onInputChange={(val) => {
              setInputValue(val);
              const user = labelValue.find((item) => item.label === val);
              const tempVal = listUser?.user.find(
                (item) => item.employee_id === user?.value,
              );
              setValueUser(tempVal);
            }}
            disabled={!!valueUser}
            id="input-nip-autocomplete"
            value={inputValue}
            label="NIP atau Email"
            error={
              labelValue.length === 0 && inputValue.length > 0 && !valueUser
                ? {
                  message: "NIP atau nama tidak ditemukan",
                }
                : undefined
            }
            leftIcon={valueUser ? undefined : <SearchIcon />}
            options={labelValue.length > 0 ? labelValue : []}
            onSelected={(val) => {
              const tempVal = listUser?.user.find(
                (item) => item.employee_id === val.value,
              );
              setValueUser(tempVal);
            }}
            onClear={() => {
              setInputValue("");
              setValueUser(undefined);
            }}
          />
        ) : null}
        <div>
          {!isEditMode && !valueUser ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                alignItems: "center",
                justifyContent: "center",
                margin: "1.5rem 0",
              }}
            >
              <img src={CostumerService} alt="" />
              <Typography level={4} fontWeight="bold">
                Silahkan tambahkan data user melalui NIP atau Email
              </Typography>
            </div>
          ) : valueUser ? (
            <UserInformation>
              <img className="avatar" src={Avatar} alt="" />
              <UserInformationText>
                <Typography level={3} fontWeight="medium" color="#444444">
                  {valueUser.employee_name}
                </Typography>
                <div>{valueUser.employee_email}</div>
              </UserInformationText>
              <img className="background-corner" src={Background} alt="" />
            </UserInformation>
          ) : null}
        </div>
        <form onSubmit={handleSubmit(() => setConfirmationModal(true))}>
          {valueUser && (
            <>
              <Controller
                control={control}
                name="type_code"
                render={({ field: { onChange, value } }) => (
                  <RadioGroupWrapper
                    required
                    title="Tipe user"
                    onChange={onChange}
                    value={value}
                  >
                    <RadioWrapper
                      $disabled
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!isEditMode) {
                          snackbar.show("Saat ini HO belum tersedia", "error");
                        }
                      }}
                    >
                      <Radio
                        label="HO"
                        disabled
                        value="O"
                      />
                    </RadioWrapper>
                    <RadioWrapper $disabled={isEditMode}>
                      <Radio
                        data-testid="pool-radio"
                        label="Pool"
                        value="P"
                        disabled={isEditMode}
                      />
                    </RadioWrapper>
                    <RadioWrapper
                      $disabled
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!isEditMode) {
                          snackbar.show("Saat ini Pangkalan belum tersedia", "error");
                        }
                      }}
                    >
                      <Radio
                        label="Pangkalan"
                        disabled
                        value="H"
                      />
                    </RadioWrapper>
                  </RadioGroupWrapper>
                )}
              />
              {isEditMode ? (
                <div style={{ marginTop: "1rem" }}>
                  <TableCurrentPositions
                    dataSource={[...currentPositions]}
                    isLoading={isLoadingCurrentPositions}
                    hasPrimaryColor={false}
                    hideTitle
                  />
                  <ReplacementUserSection>
                    <Typography level={5} fontWeight="bold" color="#444444">
                      Petugas pengganti
                    </Typography>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "1rem",
                        marginTop: "1rem",
                        width: "100%",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Controller
                          control={control}
                          name="replacement_user.nip"
                          render={({ field, fieldState: { error } }) => (
                            <Form
                              {...field}
                              id="replacement-officer-nip"
                              label="NIP"
                              maxLength={10}
                              onChange={(e) => {
                                e.target.value = e.target.value.replace(/[^0-9]/g, "");
                                field.onChange(e);
                              }}
                              error={
                                error
                                  ? { message: error.message as string }
                                  : undefined
                              }
                              required
                            />
                          )}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Controller
                          control={control}
                          name="replacement_user.nama"
                          render={({ field, fieldState: { error } }) => (
                            <Form
                              {...field}
                              id="replacement-officer-nama"
                              type="text"
                              label="Nama"
                              maxLength={100}
                              onChange={(e) => {
                                e.target.value = e.target.value.replace(/[^a-zA-Z ]/g, "");
                                field.onChange(e);
                              }}
                              error={
                                error
                                  ? { message: error.message as string }
                                  : undefined
                              }
                              required
                            />
                          )}
                        />
                      </div>
                    </div>
                  </ReplacementUserSection>
                </div>
              ) : (
                <UserPlacement>
                  <div id="outside" className="user-placement-header">
                    <div className="user-placement-header-text">
                      <Typography level={5} fontWeight="bold" color="#444444">
                        Penempatan user
                      </Typography>
                      <Typography
                        level={6}
                        fontWeight="regular"
                        color="#666666"
                      >
                        Detail penempatan & jabatan user
                      </Typography>
                    </div>
                    <div>
                      {!isEditMode && (
                        <Button
                          variant="text"
                          color="primary"
                          type="button"
                          onClick={() => append({
                            area: labelOptionValue,
                            pool: labelOptionValue,
                            position: labelOptionValue,
                            department: labelOptionValue,
                            segment: labelOptionValue,
                            roles: labelOptionValue,
                          })}
                          leftIcon={<PlusIcon />}
                        >
                          Tambah data
                        </Button>
                      )}
                    </div>
                  </div>
                  {fields.map((item, index) => (
                    <UserPositionMultipleForm
                      hideButton={fields.length === 1}
                      className="user-placement-body"
                      control={control}
                      setValue={setValue}
                      getValues={getValues}
                      remove={remove}
                      index={index}
                      item={item}
                      listAll={{
                        listArea: listAreas,
                        listPool,
                        listDepartment,
                        listJobTitle,
                        listSegments,
                        listSegment,
                        listRoles,
                      }}
                    />
                  ))}
                </UserPlacement>
              )}
            </>
          )}
          <Footer>
            <Button type="button" variant="text" onClick={() => setOpen(false)}>
              Batal
            </Button>
            <Button disabled={!isValid}>Simpan</Button>
          </Footer>
        </form>
      </div>
      {createPortal(
        <ConfirmationModal
          onSubmit={submitUser}
          loading={mutateUser.isLoading || updateUser.isLoading}
        />,
        document.body,
      )}
    </>
  );
};

export default UserPositionForm;
